import {
	AudioTrack,
	ConnectionQualityIndicator,
	ParticipantTile,
	TrackMutedIndicator,
	useTrackRefContext,
	VideoTrack,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import type { ComponentPropsWithoutRef } from "react";
import { cn } from "@/lib/utils";

type CustomParticipantTileProps = ComponentPropsWithoutRef<
	typeof ParticipantTile
> & {
	hideMutedIndicator?: boolean;
};

function CustomParticipantTileContent({
	hideMutedIndicator,
}: {
	hideMutedIndicator?: boolean;
}) {
	const trackRef = useTrackRefContext();
	const isVideoEnabled =
		trackRef.publication?.track && !trackRef.publication.isMuted;

	const micTrackRef = {
		participant: trackRef.participant,
		source: Track.Source.Microphone,
		publication: trackRef.participant.getTrackPublication(
			Track.Source.Microphone
		),
	};

	let displayName = trackRef.participant.identity;

	if (trackRef.participant.metadata) {
		try {
			const metadata = JSON.parse(trackRef.participant.metadata);
			displayName = metadata.displayName || displayName;
		} catch {
			// If metadata parsing fails, keep the identity
		}
	}

	return (
		<>
			{isVideoEnabled ? (
				<VideoTrack />
			) : (
				<div className="absolute inset-0 flex items-center justify-center bg-muted p-[15%]">
					<div className="h-full max-h-[320px] w-full max-w-[320px] [&_path]:fill-black/80 dark:[&_path]:fill-white/80">
						{/** biome-ignore lint/a11y/noSvgWithoutTitle: decorative user avatar icon */}
						<svg
							className="h-full w-full"
							fill="none"
							preserveAspectRatio="xMidYMid meet"
							viewBox="0 0 320 320"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M160 180C204.182 180 240 144.183 240 100C240 55.8172 204.182 20 160 20C115.817 20 79.9997 55.8172 79.9997 100C79.9997 144.183 115.817 180 160 180Z"
								fill="currentColor"
								fillOpacity={0.25}
							/>
							<path
								d="M97.6542 194.614C103.267 191.818 109.841 192.481 115.519 195.141C129.025 201.466 144.1 205 159.999 205C175.899 205 190.973 201.466 204.48 195.141C210.158 192.481 216.732 191.818 222.345 194.614C262.703 214.719 291.985 253.736 298.591 300.062C300.15 310.997 291.045 320 280 320H39.9997C28.954 320 19.8495 310.997 21.4087 300.062C28.014 253.736 57.2966 214.72 97.6542 194.614Z"
								fill="currentColor"
								fillOpacity={0.25}
							/>
						</svg>
					</div>
				</div>
			)}

			<AudioTrack />

			<div
				className={
					"absolute bottom-0 left-0 flex items-center gap-1.5 rounded-md border border-white/20 bg-black/60 px-2 py-1.5 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100"
				}
			>
				{!hideMutedIndicator && (
					<TrackMutedIndicator
						className="[&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:text-white"
						trackRef={micTrackRef}
					/>
				)}
				<span className="font-medium text-white text-xs">{displayName}</span>
			</div>

			<div className="absolute right-2 bottom-2">
				<ConnectionQualityIndicator className="[&_svg]:h-4 [&_svg]:w-4 [&_svg]:text-white" />
			</div>
		</>
	);
}

export function CustomParticipantTile({
	hideMutedIndicator,
	...props
}: CustomParticipantTileProps) {
	const isScreenShare = props.trackRef?.source === Track.Source.ScreenShare;

	return (
		<div className="@container-[size] flex h-full w-full items-center justify-center">
			<ParticipantTile
				{...props}
				className={cn(
					"group mx-auto h-full w-full",
					!isScreenShare && "aspect-video h-auto max-w-[177.78cqh]",
					props.className
				)}
			>
				<CustomParticipantTileContent hideMutedIndicator={hideMutedIndicator} />
			</ParticipantTile>
		</div>
	);
}
